import { useState, useEffect, useRef } from 'react';

export default function Benefits() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [visibleCards, setVisibleCards] = useState<Set<number>>(new Set());
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  // En móvil, activar animaciones con IntersectionObserver en vez de hover
  useEffect(() => {
    const isTouchDevice = window.matchMedia('(hover: none)').matches;
    if (!isTouchDevice) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const index = Number(entry.target.getAttribute('data-index'));
          if (entry.isIntersecting) {
            setVisibleCards(prev => new Set(prev).add(index));
          }
        });
      },
      { threshold: 0.4 }
    );

    cardRefs.current.forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const benefits = [
    {
      icon: 'ri-phone-line',
      title: 'Reducción de llamadas',
      description: 'Menos interrupciones y coordinación manual. La información puede fluir automáticamente.',
      metric: 'Potencial de optimización',
      note: 'Coordinación automatizada',
      story: 'El sistema facilita la comunicación entre departamentos reduciendo la necesidad de llamadas.',
      percentage: 70,
      color: '#06B6D4'
    },
    {
      icon: 'ri-time-line',
      title: 'Anticipación de altas',
      description: 'Posibilidad de planificar con mayor antelación gracias a las predicciones de IA.',
      metric: 'Planificación anticipada',
      note: 'Predicciones con IA',
      story: 'Las predicciones permiten al equipo prepararse con mayor antelación.',
      percentage: 85,
      color: '#8B5CF6'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Trazabilidad completa',
      description: 'Se registra información clave para garantizar transparencia y control.',
      metric: 'Registro completo',
      note: 'Auditoría de acciones',
      story: 'El sistema registra todas las acciones para mayor transparencia y control.',
      percentage: 100,
      color: '#10B981'
    },
    {
      icon: 'ri-team-line',
      title: 'Más tiempo para tareas clínicas',
      description: 'Reducción del tiempo, permitiendo enfocarse en la atención.',
      metric: 'Ahorro de tiempo',
      note: 'Automatización de procesos',
      story: 'La automatización libera tiempo para dedicarlo a tareas de mayor valor.',
      percentage: 75,
      color: '#F59E0B'
    },
    {
      icon: 'ri-dashboard-line',
      title: 'Visión compartida en tiempo real',
      description: 'Todo el equipo puede ver el mismo estado actualizado. Una sola fuente de verdad.',
      metric: 'Sincronización instantánea',
      note: 'Información centralizada',
      story: 'Información unificada que reduce discrepancias entre departamentos.',
      percentage: 100,
      color: '#EC4899'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Mejora en la experiencia del paciente',
      description: 'Procesos más fluidos que reducen tiempos de espera.',
      metric: 'Mejor coordinación',
      note: 'Procesos optimizados',
      story: 'Una mejor coordinación puede mejorar la experiencia general del paciente.',
      percentage: 90,
      color: '#EF4444'
    }
  ];

  return (
    <section className="py-16 sm:py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4 sm:mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
            Impacto en tu Hospital
          </h2>
          <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto px-2">
            PreBedAI es una herramienta de apoyo que puede ayudar a tu equipo a trabajar de forma más eficiente.
          </p>
        </div>

        {/* Benefits Grid with Animated Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
          {benefits.map((benefit, index) => {
            const isCardActive = hoveredIndex === index || visibleCards.has(index);
            return (
            <div 
              key={index}
              ref={el => { cardRefs.current[index] = el; }}
              data-index={index}
              className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 sm:hover:-translate-y-2 border border-gray-100 relative overflow-hidden cursor-pointer"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* Animated Background Circle */}
              <div 
                className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 rounded-full opacity-10 transition-transform duration-500"
                style={{ 
                  backgroundColor: benefit.color,
                  transform: isCardActive ? 'scale(1.5)' : 'scale(1)'
                }}
              ></div>

              <div className="relative z-10">
                <div 
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center mb-4 sm:mb-6 transition-all duration-300"
                  style={{ 
                    background: `linear-gradient(135deg, ${benefit.color}, ${benefit.color}dd)`
                  }}
                >
                  <i className={`${benefit.icon} text-2xl sm:text-3xl text-white`}></i>
                </div>
                
                <h3 className="text-lg sm:text-xl font-bold text-[#0A1628] mb-2 sm:mb-3">
                  {benefit.title}
                </h3>
                
                <p className="text-sm text-gray-600 mb-3 sm:mb-4 leading-relaxed">
                  {benefit.description}
                </p>
                
                {/* Animated Circular Progress */}
                <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div className="relative w-14 h-14 sm:w-20 sm:h-20">
                    <svg className="w-14 h-14 sm:w-20 sm:h-20 transform -rotate-90" viewBox="0 0 100 100">
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        stroke="#e5e7eb"
                        strokeWidth="8"
                        fill="none"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        stroke={benefit.color}
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={`${2 * Math.PI * 40}`}
                        strokeDashoffset={`${2 * Math.PI * 40 * (1 - (isCardActive ? benefit.percentage : 0) / 100)}`}
                        className="transition-all duration-1000 ease-out"
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-sm sm:text-lg font-bold" style={{ color: benefit.color }}>
                        {isCardActive ? benefit.percentage : 0}%
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-base sm:text-2xl font-bold" style={{ color: benefit.color }}>
                      {benefit.metric}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500 italic">
                      {benefit.note}
                    </p>
                  </div>
                </div>

                {/* Story appears on hover/scroll visibility */}
                <div 
                  className={`transition-all duration-300 overflow-hidden ${
                    isCardActive ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="pt-3 sm:pt-4 border-t border-gray-200">
                    <p className="text-xs sm:text-sm text-gray-600 italic flex items-start gap-2">
                      <i className="ri-lightbulb-line flex-shrink-0 mt-0.5" style={{ color: benefit.color }}></i>
                      <span>{benefit.story}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
          })}
        </div>

        {/* Disclaimer */}
        <div className="mt-8 sm:mt-12 bg-blue-50 border-l-4 border-[#06B6D4] p-4 sm:p-6 rounded-lg max-w-4xl mx-auto">
          <div className="flex items-start gap-2 sm:gap-3">
            <i className="ri-information-line text-[#06B6D4] text-xl sm:text-2xl flex-shrink-0 mt-0.5"></i>
            <div>
              <p className="text-gray-700 leading-relaxed text-xs sm:text-sm">
                <strong>Nota importante:</strong> Los beneficios mostrados son estimaciones basadas en el potencial de optimización que puede ofrecer el sistema. Los resultados reales pueden variar según el tamaño del centro, procesos actuales y nivel de adopción del equipo.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
